import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient, useLanguage } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Separator } from "../components/ui/separator";
import PageHeader from "../components/layout/PageHeader";
import DhikrTimer from "../components/DhikrTimer";
import { toast } from "sonner";
import {
  RotateCcw,
  Calendar,
  TrendingUp,
  Award,
  Clock,
  Sparkles,
  Volume2,
} from "lucide-react";

const Tasbih = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isEnglish = language === "en";
  const { user } = useAuth();

  // Counters for all dhikr
  const [counts, setCounts] = useState({
    subhanAllah: 0,
    alhamdulillah: 0,
    allahuAkbar: 0,
    istighfar: 0,
    shalawat: 0,
  });

  const [dailyHistory, setDailyHistory] = useState([]);
  const [totalToday, setTotalToday] = useState(0);
  const [isPremium, setIsPremium] = useState(false);

  // Helper function to get local date in YYYY-MM-DD format
  const getLocalDate = (date = new Date()) => {
    const d = date instanceof Date ? date : new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const TARGETS = {
    subhanAllah: 33,
    alhamdulillah: 33,
    allahuAkbar: 34,
    istighfar: 100,
    shalawat: 10,
  };

  const LABELS = {
    subhanAllah: {
      arabic: "سُبْحَانَ اللَّهِ",
      transliteration: "SubhanAllah",
      indonesia: "Maha Suci Allah",
      english: "Glory be to Allah",
    },
    alhamdulillah: {
      arabic: "الْحَمْدُ لِلَّهِ",
      transliteration: "Alhamdulillah",
      indonesia: "Segala puji bagi Allah",
      english: "Praise be to Allah",
    },
    allahuAkbar: {
      arabic: "اللَّهُ أَكْبَرُ",
      transliteration: "Allahu Akbar",
      indonesia: "Allah Maha Besar",
      english: "Allah is the Greatest",
    },
    istighfar: {
      arabic: "أَسْتَغْفِرُ اللَّهَ",
      transliteration: "Astaghfirullah",
      indonesia: "Aku memohon ampun kepada Allah",
      english: "I seek forgiveness from Allah",
    },
    shalawat: {
      arabic: "اللَّهُمَّ صَلِّ عَلَى مُحَمَّدٍ",
      transliteration: "Allahumma Shalli 'ala Muhammad",
      indonesia: "Ya Allah, berikanlah shalawat kepada Muhammad",
      english: "O Allah, send prayers upon Muhammad",
    },
  };

  // Load today's history
  useEffect(() => {
    loadTodayHistory();
  }, []);

  const loadTodayHistory = async () => {
    try {
      const today = getLocalDate();
      const res = await apiClient.get(`/dzikir/logs/${today}`);

      if (res.data && res.data.length > 0) {
        // Find logs for tasbih dhikr
        const subhanLog = res.data.find(l => l.dzikir_id === 'tasbih-subhanallah');
        const alhamdulillahLog = res.data.find(l => l.dzikir_id === 'tasbih-alhamdulillah');
        const allahuAkbarLog = res.data.find(l => l.dzikir_id === 'tasbih-allahuakbar');
        const istighfarLog = res.data.find(l => l.dzikir_id === 'tasbih-istighfar');
        const shalawatLog = res.data.find(l => l.dzikir_id === 'tasbih-shalawat');

        setCounts({
          subhanAllah: subhanLog?.count || 0,
          alhamdulillah: alhamdulillahLog?.count || 0,
          allahuAkbar: allahuAkbarLog?.count || 0,
          istighfar: istighfarLog?.count || 0,
          shalawat: shalawatLog?.count || 0,
        });

        setDailyHistory(res.data);
      }
    } catch (error) {
      console.error("Error loading history:", error);
    }
  };

  // Calculate total today
  useEffect(() => {
    const total = counts.subhanAllah + counts.alhamdulillah + counts.allahuAkbar + counts.istighfar + counts.shalawat;
    setTotalToday(total);
  }, [counts]);

  const incrementCount = async (key) => {
    const newCount = counts[key] + 1;
    const target = TARGETS[key];

    // Update local state
    setCounts(prev => ({
      ...prev,
      [key]: newCount,
    }));

    // Save to backend
    try {
      const dzikirId = `tasbih-${key.toLowerCase()}`;
      await apiClient.post(`/dzikir/log?dzikir_id=${dzikirId}&count=${newCount}`);
    } catch (error) {
      console.error("Error logging tasbih:", error);
    }

    // Premium vibration feedback
    if (navigator.vibrate) {
      if (newCount === target) {
        // Target reached - longer vibration pattern
        navigator.vibrate([100, 50, 100, 50, 200]);
        toast.success(
          isEnglish
            ? `${LABELS[key].english} - ${target}x complete! 🎉`
            : `${LABELS[key].indonesia} - ${target}x selesai! 🎉`
        );
      } else if (newCount % 10 === 0) {
        // Every 10 counts - double vibration
        navigator.vibrate([50, 30, 50]);
      } else {
        // Normal tap - single vibration
        navigator.vibrate(30);
      }
    }

    // Individual completion toast already handled in incrementCount above
  };

  const resetCount = (key) => {
    setCounts(prev => ({
      ...prev,
      [key]: 0,
    }));
    toast.success(isEnglish ? "Counter reset" : "Counter direset");
  };

  const resetAll = () => {
    setCounts({
      subhanAllah: 0,
      alhamdulillah: 0,
      allahuAkbar: 0,
      istighfar: 0,
      shalawat: 0,
    });
    toast.success(isEnglish ? "All counters reset" : "Semua counter direset");
  };

  const normalizeReminderTime = (timeValue, fallback) => {
    if (!timeValue) return fallback;
    const text = String(timeValue);
    if (text.length >= 5 && text[2] === ":") return text.slice(0, 5);
    return fallback;
  };

  const getLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation not supported"));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => reject(error),
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  };

  const resolveReminderTime = async (preset, fallback) => {
    try {
      const loc = await getLocation();
      const res = await apiClient.get("/prayer-times", {
        params: { latitude: loc.latitude, longitude: loc.longitude },
      });
      const timings = res.data?.timings || {};
      const timeValue = preset === "daily"
        ? timings.Isha
        : preset === "morning"
        ? timings.Fajr
        : timings.Maghrib;
      return normalizeReminderTime(timeValue, fallback);
    } catch (error) {
      return fallback;
    }
  };

  const openDzikirReminder = async (preset = "daily") => {
    const presets = {
      daily: {
        title: isEnglish ? "Daily Dhikr" : "Dzikir Harian",
        message: isEnglish ? "Time for dhikr and tasbih." : "Saatnya dzikir dan tasbih.",
        reminder_time: "08:00",
        frequency: "daily"
      },
      morning: {
        title: isEnglish ? "Morning Dhikr" : "Dzikir Pagi",
        message: isEnglish ? "Start your day with dhikr." : "Mulai hari dengan dzikir.",
        reminder_time: "05:00",
        frequency: "daily"
      },
      evening: {
        title: isEnglish ? "Evening Dhikr" : "Dzikir Petang",
        message: isEnglish ? "End your day with dhikr." : "Akhiri hari dengan dzikir.",
        reminder_time: "18:00",
        frequency: "daily"
      }
    };
    const selectedPreset = presets[preset] || presets.daily;
    const reminderTime = await resolveReminderTime(preset, selectedPreset.reminder_time);
    navigate("/reminders", {
      state: {
        prefillReminder: {
          title: selectedPreset.title,
          message: selectedPreset.message,
          reminder_time: reminderTime,
          frequency: selectedPreset.frequency
        }
      }
    });
  };

  const getProgressColor = (count, target) => {
    if (count >= target) return "bg-green-500";
    if (count >= target * 0.7) return "bg-blue-500";
    if (count >= target * 0.4) return "bg-yellow-500";
    return "bg-muted";
  };

  const CounterButton = ({ type, data }) => {
    const count = counts[type] || 0;
    const target = TARGETS[type];
    const progress = count > 0 ? (count / target) * 100 : 0;
    const isComplete = count >= target;

    return (
      <Card className={`${isComplete ? "border-green-500 border-2" : ""}`}>
        <CardContent className="p-6">
          <div className="space-y-4">
            {/* Arabic Text */}
            <div className="text-center">
              <p className="font-arabic text-3xl mb-2" dir="rtl">
                {data.arabic}
              </p>
              <p className="text-sm text-muted-foreground">
                {data.transliteration}
              </p>
              <Badge variant="secondary" className="mt-2">
                {isEnglish ? data.english : data.indonesia}
              </Badge>
            </div>

            {/* Counter Button */}
            <div className="flex justify-center my-4">
              <button
                className="tasbih-counter-btn"
                style={{
                  background: isComplete
                    ? "linear-gradient(to right, rgb(34, 197, 94), rgb(16, 185, 129))"
                    : "linear-gradient(to bottom right, hsl(168, 76%, 32%), hsl(168, 76%, 32%, 0.8))",
                  fontSize: count === 0 ? "1.5rem" : "2.5rem"
                }}
                onClick={() => incrementCount(type)}
              >
                {count === 0 ? (isEnglish ? "Start" : "Mulai") : count}
              </button>
            </div>

            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {isEnglish ? "Progress" : "Progres"}
                </span>
                <span className={isComplete ? "text-green-600 font-semibold" : ""}>
                  {count}/{target}
                </span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            {/* Reset Button */}
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => resetCount(key)}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              {isEnglish ? "Reset" : "Reset"}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen" data-testid="tasbih-page">
      <PageHeader
        title={isEnglish ? "Digital Tasbih" : "Tasbih Digital"}
        showBack
        onBackClick={() => navigate("/doa")}
      />

      <div className="page-container space-y-6">
        {/* Header Card */}
        <Card className="islamic-gradient text-white">
          <CardContent className="p-6 text-center">
            <div className="flex items-center justify-center mb-2">
              <Sparkles className="w-5 h-5 mr-2" />
              <Badge className="bg-white/20 text-white border-0">
                {isEnglish ? "Premium" : "Premium"}
              </Badge>
            </div>
            <h2 className="text-2xl font-bold mb-2">
              {isEnglish ? "Morning & Evening Dhikr" : "Dzikir Pagi & Petang"}
            </h2>
            <p className="text-white/80 text-sm mb-4">
              {isEnglish
                ? "Morning & Evening Dhikr + Additional Dhikr"
                : "Dzikir Pagi & Petang + Dzikir Tambahan"}
            </p>

            {/* Total Progress */}
            <div className="bg-white/10 rounded-xl p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm">
                  {isEnglish ? "Total Today" : "Total Hari Ini"}
                </span>
                <span className="text-2xl font-bold">{totalToday}</span>
              </div>
              <Progress value={Math.min((totalToday / 210) * 100, 100)} className="h-3 bg-white/20" />
            </div>
          </CardContent>
        </Card>

        <Button variant="outline" className="w-full" onClick={() => openDzikirReminder("daily")}>
          <Clock className="w-4 h-4 mr-2" />
          {isEnglish ? "Set Dhikr Reminder" : "Atur Pengingat Dzikir"}
        </Button>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button variant="outline" onClick={() => openDzikirReminder("morning")}>
            <Clock className="w-4 h-4 mr-2" />
            {isEnglish ? "Set Morning Dhikr" : "Atur Dzikir Pagi"}
          </Button>
          <Button variant="outline" onClick={() => openDzikirReminder("evening")}>
            <Clock className="w-4 h-4 mr-2" />
            {isEnglish ? "Set Evening Dhikr" : "Atur Dzikir Petang"}
          </Button>
        </div>

        {/* Dhikr Timer */}
        <DhikrTimer isEnglish={isEnglish} />

        {/* Counters */}
        <div className="space-y-4">
          <CounterButton key="btn-subhanAllah" type="subhanAllah" data={LABELS.subhanAllah} />
          <Separator />
          <CounterButton key="btn-alhamdulillah" type="alhamdulillah" data={LABELS.alhamdulillah} />
          <Separator />
          <CounterButton key="btn-allahuAkbar" type="allahuAkbar" data={LABELS.allahuAkbar} />
          <Separator />
          <CounterButton key="btn-istighfar" type="istighfar" data={LABELS.istighfar} />
          <Separator />
          <CounterButton key="btn-shalawat" type="shalawat" data={LABELS.shalawat} />
        </div>

        {/* Reset All Button */}
        <Button
          variant="outline"
          className="w-full"
          onClick={resetAll}
          disabled={totalToday === 0}
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          {isEnglish ? "Reset All Counters" : "Reset Semua Counter"}
        </Button>

        {/* Info Card */}
        <Card className="bg-muted/50">
          <CardContent className="p-5">
            <h3 className="font-semibold mb-3 flex items-center">
              <Award className="w-5 h-5 mr-2 text-primary" />
              {isEnglish ? "Benefits & Rewards" : "Keutamaan & Pahala"}
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  {isEnglish
                    ? "Recited after every prayer (obligatory)"
                    : "Dibaca setelah shalat wajib"}
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  {isEnglish
                    ? "100 good deeds recorded for you"
                    : "100 kebaikan dicatat untukmu"}
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  {isEnglish
                    ? "100 sins forgiven for you"
                    : "100 dosa diampunkan bagimu"}
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  {isEnglish
                    ? "Istighfar: Brings relief from difficulties & blessings (Rahman)"
                    : "Istighfar: Mendatangkan kemudahan & keberkahan (Ar-Rahman)"}
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  {isEnglish
                    ? "Shalawat: Raises your status & brings you closer to Prophet ﷺ"
                    : "Shalawat: Mengangkat derajat & mendekatkan kepada Nabi ﷺ"}
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  {isEnglish
                    ? "Protection from poverty and misery"
                    : "Perlindungan dari kemiskinan dan kesengsaraan"}
                </span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* History Section */}
        {totalToday > 0 && (
          <Card>
            <CardContent className="p-5">
              <h3 className="font-semibold mb-4 flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                {isEnglish ? "Today's Progress" : "Progres Hari Ini"}
              </h3>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{counts.subhanAllah}</p>
                  <p className="text-xs text-muted-foreground">SubhanAllah</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{counts.alhamdulillah}</p>
                  <p className="text-xs text-muted-foreground">Alhamdulillah</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">{counts.allahuAkbar}</p>
                  <p className="text-xs text-muted-foreground">Allahu Akbar</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-600">{counts.istighfar}</p>
                  <p className="text-xs text-muted-foreground">Istighfar</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-pink-600">{counts.shalawat}</p>
                  <p className="text-xs text-muted-foreground">Shalawat</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* View Full History Button */}
        <Button
          variant="outline"
          className="w-full"
          onClick={() => navigate("/doa/history")}
        >
          <Calendar className="w-4 h-4 mr-2" />
          {isEnglish ? "View Dhikr History" : "Lihat Riwayat Dzikir"}
        </Button>
      </div>
    </div>
  );
};

export default Tasbih;
